// SchedulingEmployer.js
import Lottie from "lottie-react";
import loadingAnimation from "../tools/Animations/Loading Dots Blue.json";

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  forwardRef,
} from "react";
import { auth, db } from "../firebase/config";
import { DateTime } from "luxon";

import ExistingScheduleSection from "../components/SchedulingEmployer/ExistingScheduleSection";
import CreateScheduleSection from "../components/SchedulingEmployer/CreateScheduleSection";

import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  query,
  where,
  setDoc,
  serverTimestamp,
  writeBatch, // ← add this
  runTransaction, // ✅ add
  increment, // ✅ add
} from "firebase/firestore";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
//import ScheduleTabs from "../components/ScheduleTabs"; // Create this component
import "../styles/SchedulingEmployer.css";

import {
  getSchedulingConfigured,
  setSchedulingConfigured,
} from "../utils/firstRunFlags";
//import { SlidersHorizontal } from "lucide-react";
import {
  Settings,
  Clock3,
  Info,
  Building2,
  Users,
  ShieldCheck,
  Save,
  Globe2,
} from "lucide-react";

function Loader({ label = "Loading...", overlay = false, small = false }) {
  const size = small ? 72 : 140;
  return (
    <div className={overlay ? "loader-overlay" : "loader-inline"}>
      <Lottie
        animationData={loadingAnimation}
        loop
        style={{ width: size, height: size }}
      />
      {label && <div className="loader-label">{label}</div>}
    </div>
  );
}

function ConfirmDialog({
  open,
  title = "Are you sure?",
  body = "",
  confirmText = "Confirm",
  cancelText = "Cancel",
  danger = false,
  onConfirm,
  onCancel,
}) {
  if (!open) return null;
  return (
    <div className="modal-backdrop">
      <div className="modal-content" style={{ maxWidth: 460 }}>
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        {body && <p style={{ marginTop: 8 }}>{body}</p>}
        <div className="modal-actions">
          <button
            type="button"
            onClick={onConfirm}
            style={{ background: danger ? "#b91c1c" : undefined }}
          >
            {confirmText}
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{ background: "#6b7280" }}
          >
            {cancelText}
          </button>
        </div>
      </div>
    </div>
  );
}

function LimitsWarningModal({
  open,
  title = "Hours limit exceeded",
  violations = [],
  onProceed,
  onCancel,
  isProceeding = false,
}) {
  if (!open) return null;

  // Simple formatter to make each line look cleaner
  const formatLine = (line) => {
    const raw = String(line || "").trim();

    // Example: "Emp1: weekly cap exceeded (63.00/40.00 hrs)"
    // Example: "Company weekly cap exceeded: 120/100 hrs"
    if (raw.includes(":")) {
      const [left, ...rest] = raw.split(":");
      return {
        label: left.trim(),
        text: rest.join(":").trim(),
        kind: left.toLowerCase().includes("company") ? "company" : "employee",
      };
    }

    return { label: "Limit", text: raw, kind: "other" };
  };

  const items = (violations || []).map(formatLine);
  const count = items.length;

  return (
    <div
      className="lw-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lw-title"
      onMouseDown={(e) => {
        // Click outside closes
        if (e.target === e.currentTarget && !isProceeding) onCancel?.();
      }}
    >
      <div className="lw-modal" role="document">
        {/* Header */}
        <div className="lw-header">
          <div className="lw-head-left">
            <div className="lw-icon" aria-hidden>
              !
            </div>
            <div>
              <div id="lw-title" className="lw-title">
                {title}
              </div>
              <div className="lw-sub">
                This schedule exceeds one or more configured hour caps.
              </div>
            </div>
          </div>

          <button
            type="button"
            className="lw-close"
            aria-label="Close warning"
            onClick={onCancel}
            disabled={isProceeding}
          >
            ✕
          </button>
        </div>

        {/* Summary */}
        <div className="lw-summary">
          <div className="lw-summary-left">
            <span className="lw-pill lw-pill-warn">
              {count} {count === 1 ? "violation" : "violations"}
            </span>
            <span className="lw-pill lw-pill-soft">Override available</span>
          </div>

          <div className="lw-summary-right">
            <span className="lw-note">
              If you proceed, the schedule will still be saved.
            </span>
          </div>
        </div>

        {/* List */}
        <div className="lw-list" role="list">
          {items.map((it, idx) => (
            <div className="lw-item" role="listitem" key={idx}>
              <div className="lw-item-left">
                <div
                  className={`lw-badge ${
                    it.kind === "company"
                      ? "lw-badge-company"
                      : it.kind === "employee"
                      ? "lw-badge-emp"
                      : "lw-badge-other"
                  }`}
                >
                  {it.kind === "company"
                    ? "Company"
                    : it.kind === "employee"
                    ? "Employee"
                    : "Limit"}
                </div>
                <div className="lw-item-main">
                  <div className="lw-item-label">{it.label}</div>
                  <div className="lw-item-text">{it.text}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="lw-footer">
          <button
            type="button"
            className="lw-btn lw-btn-ghost"
            onClick={onCancel}
            disabled={isProceeding}
          >
            Cancel
          </button>

          <button
            type="button"
            className="lw-btn lw-btn-danger"
            onClick={onProceed}
            disabled={isProceeding}
          >
            {isProceeding ? (
              <>
                <span className="lw-spinner" aria-hidden />
                Proceeding…
              </>
            ) : (
              "Proceed anyway"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// === TOP OF FILE, ABOVE export default function ===
const SELECT_Z = 4000;
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: SELECT_Z }),
  menu: (base) => ({ ...base, zIndex: SELECT_Z }),
};

const markScheduleMissed = async (companyId, scheduleId, dateISO) => {
  const ref = doc(db, `companies/${companyId}/schedules`, scheduleId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;

    const data = snap.data() || {};
    const missedDates = data.missedDates || {};
    const alreadyMarked = missedDates?.[dateISO] === true;

    // ✅ If already marked, do nothing (prevents double-counting)
    if (alreadyMarked) return;

    tx.update(ref, {
      [`missedDates.${dateISO}`]: true,
      missedDatesCount: increment(1), // ✅ atomic + idempotent
    });
  });
};

// Write/merge a single notification document by fixed id (idempotent)
const sendCompanyNotification = async (companyId, docId, payload) => {
  const nref = doc(db, `companies/${companyId}/notifications`, docId);
  await setDoc(
    nref,
    { archived: false, read: false, timestamp: serverTimestamp(), ...payload },
    { merge: true },
  );
};
// ✅ NEW: Write/merge a user notification (employee-facing) by fixed id (idempotent)
const sendUserNotification = async (userId, docId, payload) => {
  if (!userId) return;
  const nref = doc(db, `users/${userId}/notifications`, docId);

  await setDoc(
    nref,
    {
      archived: false,
      read: false,

      // ✅ MUST MATCH YOUR BELL QUERY
      createdAt: serverTimestamp(),

      ...payload,
    },
    { merge: true },
  );
};

// Weekly Overview (REPLACE THE WHOLE FUNCTION WITH THIS)
function WeeklyOverviewSection({
  employees,
  weeklyOverviewStart,
  setWeeklyOverviewStart,
  weeklyOverviewData,
  fetchWeeklyOverview,
  toMin,
  isOverviewLoading = false,
  companyTimeZone,
  workHoursByEmpDate,
  hasEntryByEmpDate,
  companyId,
  weeklyLeaveMap,
}) {
  const [filteredEmployeeId, setFilteredEmployeeId] = useState(null);
  const ensureStartOfWeek = () => {
    // If a week is already selected, just use it
    if (weeklyOverviewStart) return weeklyOverviewStart;

    // Otherwise default to the current week in the company time zone
    const base = DateTime.now()
      .setZone(companyTimeZone)
      .startOf("week")
      .toJSDate();

    // Save + fetch for that week
    setWeeklyOverviewStart(base);
    fetchWeeklyOverview(base);

    return base;
  };

  const GRACE_MIN = 0;

  const isMissed = useCallback(
    (shift, dateISO, empId) => {
      // Don’t flag missed on off/holiday/explicit leave
      if (!shift || shift.isOffDay || shift.isHoliday || shift.isCallOut) {
        return false;
      }

      // NEW: don’t flag missed if there is approved leave for that day
      const leaveInfo = weeklyLeaveMap?.[empId]?.[dateISO] || null;
      const { hasLeave, isHalfDay } = getLeaveKind(leaveInfo);

      // ✅ Only FULL-day leave blocks missed
      if (hasLeave && !isHalfDay) return false;

      const day = DateTime.fromISO(dateISO, { zone: companyTimeZone });
      const now = DateTime.now().setZone(companyTimeZone);

      const [eh, em] = (shift.stopTime || "00:00").split(":").map(Number);

      const sMin = toMin(shift.startTime);
      const eMin = toMin(shift.stopTime);
      const overnight = eMin <= sMin;

      let end = day.set({ hour: eh, minute: em, second: 0, millisecond: 0 });

      if (overnight) end = end.plus({ days: 1 });

      end = end.plus({ minutes: GRACE_MIN });

      const pastEnd =
        day.startOf("day") < now.startOf("day") ||
        (day.hasSame(now, "day") && now > end);

      if (!pastEnd) return false;

      const hasAnyEntry = !!hasEntryByEmpDate?.[empId]?.[dateISO];
      const worked = Number(workHoursByEmpDate?.[empId]?.[dateISO] || 0);

      return !hasAnyEntry || worked <= 0;
    },
    [
      companyTimeZone,
      hasEntryByEmpDate,
      workHoursByEmpDate,
      weeklyLeaveMap,
      toMin,
    ],
  );
  const WeekRangeInput = forwardRef(({ value, onClick, displayValue }, ref) => {
    return (
      <input
        ref={ref}
        className="wo-week-input"
        onClick={onClick}
        readOnly
        value={displayValue || value || ""}
        placeholder="Select week"
      />
    );
  });
  WeekRangeInput.displayName = "WeekRangeInput";
  const normalizeStartDay = useCallback(
    (jsDate) => {
      return DateTime.fromJSDate(jsDate, { zone: companyTimeZone })
        .startOf("day")
        .toJSDate();
    },
    [companyTimeZone],
  );

  const weekDates = useMemo(() => {
    if (!weeklyOverviewStart) return [];

    const start = DateTime.fromJSDate(weeklyOverviewStart, {
      zone: companyTimeZone,
    }).startOf("day");

    return Array.from({ length: 7 }, (_, i) => {
      const d = start.plus({ days: i });
      return {
        date: d.toISODate(), // ✅ company TZ ISO date
        label: d.toFormat("ccc, LLL d"), // e.g. Tue, Jan 6
      };
    });
  }, [weeklyOverviewStart, companyTimeZone]);

  useEffect(() => {
    // On first load: default to "this week" in company TZ + All Employees
    if (!weeklyOverviewStart && companyTimeZone) {
      const start = DateTime.now()
        .setZone(companyTimeZone)
        .startOf("week")
        .toJSDate();

      setWeeklyOverviewStart(start);
      fetchWeeklyOverview(start);

      // keep filter at All Employees (null)
      setFilteredEmployeeId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyOverviewStart, companyTimeZone]);

  useEffect(() => {
    if (!weeklyOverviewStart || !weeklyOverviewData?.length || !companyId)
      return;

    const nowTz = DateTime.now().setZone(companyTimeZone);
    const todayISO = nowTz.toISODate();

    // ✅ Build week dates in COMPANY TZ (avoid toISOString() UTC shifting)
    const start = DateTime.fromJSDate(weeklyOverviewStart, {
      zone: companyTimeZone,
    }).startOf("day");
    const dates = Array.from({ length: 7 }, (_, i) =>
      start.plus({ days: i }).toISODate(),
    );

    (async () => {
      for (const sched of weeklyOverviewData) {
        const empId = sched.employeeId;
        const empName = sched.employeeName || "Employee";

        for (const dateISO of dates) {
          const shift = (sched.shifts || []).find((s) => s.date === dateISO);

          // ✅ If it's missed (and not off/holiday/leave), mark it
          if (!isMissed(shift, dateISO, empId)) continue;

          // ✅ Make sure the shift window is truly over (extra safety)
          const [eh, em] = (shift?.stopTime || "00:00").split(":").map(Number);
          let end = DateTime.fromISO(dateISO, { zone: companyTimeZone }).set({
            hour: eh || 0,
            minute: em || 0,
            second: 0,
            millisecond: 0,
          });

          // overnight support
          const sMin = toMin(shift?.startTime);
          const eMin = toMin(shift?.stopTime);
          const overnight = eMin <= sMin;
          if (overnight) end = end.plus({ days: 1 });

          // only after shift end
          if (nowTz <= end) continue;

          // ✅ ALWAYS mark missedDates for past + today
          if (sched.id) {
            await markScheduleMissed(companyId, sched.id, dateISO);
          }

          // ✅ OPTIONAL: Only send company notification for TODAY (avoid backfilling spam)
          if (dateISO === todayISO) {
            const notifId = `missedShift_${empId}_${dateISO}`;
            await sendCompanyNotification(companyId, notifId, {
              type: "missedShift",
              employeeId: empId,
              employeeName: empName,
              date: dateISO,
              scheduledStart: shift?.startTime,
              scheduledEnd: shift?.stopTime,
              severity: "high",
              reason: "noClockIn",
              message: `${empName} missed the ${shift?.startTime}–${
                shift?.stopTime
              } shift on ${nowTz.toFormat("ccc, dd LLL")}.`,
            });
          }
        }
      }
    })().catch(console.error);
  }, [
    weeklyOverviewStart,
    weeklyOverviewData,
    companyId,
    companyTimeZone,
    isMissed,
    toMin,
  ]);

  const getHourlyRate = (emp) => {
    if (!emp) return 0;

    // support multiple field names (match your PayrollReport approach)
    const raw =
      emp.hourlyRate ??
      emp.hourly_rate ??
      emp.payRate ??
      emp.pay_rate ??
      emp.rate ??
      0;

    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const formatMoney = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(n || 0));
  const getLeaveKind = (leave) => {
    if (!leave) return { hasLeave: false, isHalfDay: false };

    const tod = String(leave.timeOfDay || "").toLowerCase();

    const hasCustomWindow = !!(leave.customStart && leave.customEnd);

    // ✅ Treat these as half-day:
    // - explicit half-day strings
    // - custom time window
    const isHalfDay =
      tod.includes("half") ||
      tod.includes("am") ||
      tod.includes("pm") ||
      tod.includes("custom") ||
      hasCustomWindow;

    return { hasLeave: true, isHalfDay };
  };

  const formatLeaveWindow = (leave) => {
    if (!leave) return "";

    const start = (leave.customStart || "").trim();
    const end = (leave.customEnd || "").trim();

    // If employee submitted custom times, show them
    if (start && end) return `${start} – ${end}`;

    const tod = String(leave.timeOfDay || "").toLowerCase();

    // Otherwise fall back to timeOfDay label
    if (tod.includes("am")) return "Half-day (AM)";
    if (tod.includes("pm")) return "Half-day (PM)";
    if (tod.includes("half")) return "Half-day";

    return "Leave";
  };

  const calculateHours = (shift) => {
    if (!shift || shift.isOffDay || shift.isHoliday || shift.isCallOut)
      return 0;

    const sMin = toMin(shift.startTime);
    const eMin = toMin(shift.stopTime);
    const base = eMin > sMin ? eMin - sMin : 1440 - sMin + eMin; // overnight aware

    const acts = shift.activities || [];
    let delta = 0;
    for (const a of acts) {
      const dur =
        (toMin(a.end) <= toMin(a.start) ? toMin(a.end) + 1440 : toMin(a.end)) -
        toMin(a.start);

      delta += a.paid ? dur : -dur;
    }
    return Math.max(0, base + delta) / 60;
  };
  const weekRangeLabel = useMemo(() => {
    if (!weeklyOverviewStart) return "";

    const ws = DateTime.fromJSDate(weeklyOverviewStart, {
      zone: companyTimeZone,
    }).startOf("day");
    const we = ws.plus({ days: 6 });

    return `${ws.toFormat("MM/dd/yyyy")} – ${we.toFormat("MM/dd/yyyy")}`;
  }, [weeklyOverviewStart, companyTimeZone]);

  const employeeRows = employees
    .filter((emp) => !filteredEmployeeId || emp.id === filteredEmployeeId)
    .map((emp) => {
      // ✅ If there's no schedule, still create an empty schedule
      // so we can render leave + worked hours in the grid.
      const empSchedule = weeklyOverviewData.find(
        (s) => s.employeeId === emp.id,
      ) || {
        employeeId: emp.id,
        shifts: [],
      };

      const dailyShifts = weekDates.map((d) =>
        (empSchedule.shifts || []).find((s) => s.date === d.date),
      );

      const totalHours = dailyShifts.reduce(
        (sum, shift) => sum + calculateHours(shift),
        0,
      );

      return { emp, dailyShifts, totalHours };
    });

  const totalAllScheduled = employeeRows.reduce((s, r) => s + r.totalHours, 0);
  const totalAllWorked = employees
    .filter((emp) => !filteredEmployeeId || emp.id === filteredEmployeeId)
    .reduce(
      (sum, emp) =>
        sum +
        weekDates.reduce(
          (acc, d) => acc + Number(workHoursByEmpDate?.[emp.id]?.[d.date] || 0),
          0,
        ),
      0,
    );

  const totalScheduledEarnings = useMemo(() => {
    return employeeRows.reduce((sum, row) => {
      const rate = getHourlyRate(row.emp);
      return sum + row.totalHours * rate;
    }, 0);
  }, [employeeRows]);

  const totalActualEarnings = useMemo(() => {
    return employeeRows.reduce((sum, row) => {
      const rate = getHourlyRate(row.emp);

      const workedTotal = weekDates.reduce(
        (acc, d) =>
          acc + Number(workHoursByEmpDate?.[row.emp.id]?.[d.date] || 0),
        0,
      );

      return sum + workedTotal * rate;
    }, 0);
  }, [employeeRows, weekDates, workHoursByEmpDate]);

  const filteredEmps = employees.filter(
    (emp) => !filteredEmployeeId || emp.id === filteredEmployeeId,
  );

  const handleExportCsv = () => {
    if (!weekDates.length) return;

    const rows = [];
    rows.push(
      [
        "Employee",
        "Date",
        "Worksite",
        "Shift",
        "Scheduled Hours",
        "Worked Hours",
        "Missed",
      ].join(","),
    );

    filteredEmps.forEach((emp) => {
      const sched = weeklyOverviewData.find((s) => s.employeeId === emp.id);
      weekDates.forEach(({ date }) => {
        const shift = sched?.shifts.find((s) => s.date === date);
        const scheduled = calculateHours(shift);
        const worked = Number(workHoursByEmpDate?.[emp.id]?.[date] || 0);
        const missed = isMissed(shift, date, emp.id) ? "Yes" : "No";
        const window =
          shift && !shift.isOffDay && !shift.isHoliday && !shift.isCallOut
            ? `${formatTo12Hour(shift.startTime)} - ${formatTo12Hour(
                shift.stopTime,
              )}`
            : shift?.isCallOut
            ? "Call-out"
            : "";

        const worksite = shift?.locationName || "Main Office";

        rows.push(
          [
            `"${emp.name}"`,
            date,
            `"${worksite}"`,
            window,
            scheduled.toFixed(2),
            worked.toFixed(2),
            missed,
          ].join(","),
        );
      });
    });

    const blob = new Blob([rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `weekly-overview_${weekDates[0].date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatTo12Hour = (hhmm) => {
    if (!hhmm) return "";

    return DateTime.fromFormat(hhmm, "HH:mm")
      .toFormat("h:mm a")
      .toLowerCase(); // optional: 8:00 am style
  };

  return (
    <div className="schedule-card-container">
      {/* CONTROLS BAR */}
      <div className="wo-controls">
        <div className="wo-left">
          <label>Select Week</label>
          <div className="wo-weekpicker">
            <button
              type="button"
              className="btn-secondary2"
              onClick={() => {
                const base = ensureStartOfWeek(); // ✅ use safe base
                const start = DateTime.fromJSDate(base)
                  .minus({ weeks: 1 })
                  .toJSDate();
                setWeeklyOverviewStart(start);
                fetchWeeklyOverview(start);
              }}
            >
              ◀
            </button>

            <DatePicker
              selected={weeklyOverviewStart}
              onChange={(date) => {
                if (!date) return;

                const start = normalizeStartDay(date);
                setWeeklyOverviewStart(start);
                fetchWeeklyOverview(start);
              }}
              customInput={<WeekRangeInput displayValue={weekRangeLabel} />}
            />

            <button
              type="button"
              className="btn-secondary2"
              onClick={() => {
                const base = ensureStartOfWeek(); // ✅ use safe base
                const start = DateTime.fromJSDate(base)
                  .plus({ weeks: 1 })
                  .toJSDate();
                setWeeklyOverviewStart(start);
                fetchWeeklyOverview(start);
              }}
            >
              ▶
            </button>

            <button
              type="button"
              className="btn-secondary3"
              onClick={() => {
                const start = DateTime.now()
                  .setZone(companyTimeZone)
                  .startOf("week")
                  .toJSDate();
                setWeeklyOverviewStart(start);
                fetchWeeklyOverview(start);
              }}
            >
              This week
            </button>
            <button
              type="button"
              className="btn-secondary4"
              onClick={handleExportCsv}
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="wo-right">
          <label>Filter by Employee</label>
          <Select
            options={[
              { value: null, label: "All Employees" },
              ...employees.map((emp) => ({
                value: emp.id,
                label: `${emp.name} (${emp.role || "Employee"})`,
              })),
            ]}
            value={
              filteredEmployeeId
                ? {
                    value: filteredEmployeeId,
                    label:
                      employees.find((e) => e.id === filteredEmployeeId)
                        ?.name || "Employee",
                  }
                : { value: null, label: "All Employees" }
            }
            onChange={(opt) => setFilteredEmployeeId(opt?.value ?? null)}
            isClearable={false}
            menuPortalTarget={
              typeof document !== "undefined" ? document.body : null
            }
            menuPosition="fixed"
            menuShouldScrollIntoView={false}
            styles={selectStyles}
          />
        </div>
      </div>

      {isOverviewLoading ? (
        <Loader label="Loading overview..." />
      ) : (
        weeklyOverviewStart && (
          <>
            {/* KPI CARDS */}
            <div className="wo-kpis">
              <div className="kpi">
                <div className="kpi-label">Scheduled</div>
                <div className="kpi-value">
                  {totalAllScheduled.toFixed(1)} hrs
                </div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Worked</div>
                <div className="kpi-value">{totalAllWorked.toFixed(1)} hrs</div>
              </div>
              {/* ✅ NEW */}
              <div className="kpi kpi-money">
                <div className="kpi-label">Scheduled earnings</div>
                <div className="kpi-value">
                  {formatMoney(totalScheduledEarnings)}
                </div>
              </div>

              {/* ✅ NEW */}
              <div className="kpi kpi-money">
                <div className="kpi-label">Worked earnings</div>
                <div className="kpi-value">
                  {formatMoney(totalActualEarnings)}
                </div>
              </div>

              <div className="kpi kpi-warn">
                <div className="kpi-label">Missed shifts</div>
                <div className="kpi-value">
                  {employeeRows.reduce(
                    (count, { emp, dailyShifts }) =>
                      count +
                      dailyShifts.reduce(
                        (c, sh, i) =>
                          c + (isMissed(sh, weekDates[i].date, emp.id) ? 1 : 0),
                        0,
                      ),
                    0,
                  )}
                </div>
              </div>
            </div>

            {/* CALENDAR */}
            <div className="calendar-overview">
              <div className="calendar-row calendar-header">
                <div className="calendar-cell sticky-col">Employee</div>
                {weekDates.map((day, idx) => (
                  <div className="calendar-cell" key={idx}>
                    {day.label}
                  </div>
                ))}
                <div className="calendar-cell total-cell">Total</div>
              </div>

              {employeeRows.map(({ emp, dailyShifts, totalHours }) => {
                const workedTotal = weekDates.reduce(
                  (sum, d) =>
                    sum + Number(workHoursByEmpDate?.[emp.id]?.[d.date] || 0),
                  0,
                );
                const pct =
                  totalHours > 0
                    ? Math.min(100, (workedTotal / totalHours) * 100)
                    : 0;

                return (
                  <div className="calendar-row" key={emp.id}>
                    {/* Sticky employee cell */}
                    <div className="calendar-cell sticky-col emp-cell">
                      <div className="emp-chip">
                        {(emp.name || "?")
                          .split(/\s+/)
                          .map((s) => s[0])
                          .join("")
                          .slice(0, 2)}
                      </div>

                      <div className="emp-meta">
                        <div className="emp-name">{emp.name}</div>

                        {/* Hourly rate only */}
                        <div className="emp-rate">
                          {(() => {
                            const rate = getHourlyRate(emp);
                            return rate > 0
                              ? `${formatMoney(rate)}/hr`
                              : "—/hr";
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* 7 day cells */}
                    {dailyShifts.map((shift, idx) => {
                      const dayISO = weekDates[idx].date;
                      const worked = Number(
                        workHoursByEmpDate?.[emp.id]?.[dayISO] || 0,
                      );
                      const missed = isMissed(shift, dayISO, emp.id);

                      const hasShift = !!shift;
                      const isOff = !!shift?.isOffDay;
                      const isHoliday = !!shift?.isHoliday;
                      const isCallOut = !!shift?.isCallOut;

                      const leaveInfo =
                        weeklyLeaveMap?.[emp.id]?.[dayISO] || null;
                      const { hasLeave, isHalfDay } = getLeaveKind(leaveInfo);

                      const isWorkingShift =
                        hasShift &&
                        !isOff &&
                        !isHoliday &&
                        !isCallOut &&
                        (!hasLeave || isHalfDay);

                      // ✅ Full-day leave blocks shift display
                      const isFullDayLeave = hasLeave && !isHalfDay;

                      // ✅ Truly empty day (no shift and no full-day leave)
                      const isEmptyDay = !hasShift && !isFullDayLeave;

                      const leaveLabel =
                        hasLeave && leaveInfo?.isPaid
                          ? "Paid leave"
                          : hasLeave
                          ? "Unpaid leave"
                          : "";

                      const leaveWindowLabel = isHalfDay
                        ? formatLeaveWindow(leaveInfo)
                        : "";

                      const cellClass = isFullDayLeave
                        ? "leave"
                        : isOff
                        ? "off"
                        : isHoliday
                        ? "holiday"
                        : isCallOut
                        ? "callout"
                        : isWorkingShift
                        ? "working"
                        : "empty";

                      return (
                        <div className={`calendar-cell ${cellClass}`} key={idx}>
                          {missed && !isFullDayLeave && (
                            <span className="chip2 chip-missed2">Missed</span>
                          )}
                          {/* 👇 NO SHIFT INDICATOR */}
                          {/* ✅ No shift text ONLY when truly empty */}
                          {isEmptyDay && (
                            <div className="no-shift-label">No shift</div>
                          )}

                          {/* ✅ Worksite name */}
                          {!isFullDayLeave &&
                            shift &&
                            !shift.isOffDay &&
                            !shift.isHoliday &&
                            !shift.isCallOut && (
                              <div className="worksite-line">
                                {shift.locationName || "Main Office"}
                              </div>
                            )}

                          {isWorkingShift && (
                            <div className="scheduled-line">
                              <strong>
                                {calculateHours(shift).toFixed(1)} hrs
                              </strong>
                            </div>
                          )}

                          <div className="slot-main">
                            <strong>
                              {isFullDayLeave
                                ? leaveLabel || "Leave (approved)"
                                : shift?.isOffDay
                                ? "Off"
                                : shift?.isHoliday
                                ? "Holiday"
                                : shift?.isCallOut
                                ? "Call-out"
                                : shift
                                ? `${formatTo12Hour(
                                    shift.startTime,
                                  )} – ${formatTo12Hour(shift.stopTime)}`
                                : ""}
                            </strong>
                          </div>

                          {isWorkingShift && (
                            <div className="worked-line">
                              <strong>{worked.toFixed(2)} hrs</strong>
                            </div>
                          )}

                          {hasLeave && isHalfDay && (
                            <div className="leave-mini">
                              <div className="leave-mini-label">
                                {leaveLabel}
                              </div>
                              <div className="leave-mini-window">
                                {leaveWindowLabel}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Totals with progress */}
                    <div className="calendar-cell total-cell">
                      <div className="meter">
                        <span style={{ width: `${pct}%` }} />
                      </div>
                      <div className="meter-label">
                        {workedTotal.toFixed(1)} / {totalHours.toFixed(1)} hrs
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )
      )}
    </div>
  );
}

function ManageActivityTypesModal({
  open,
  onClose,
  types,
  onCreate,
  onUpdate,
  onDelete,
  isLoading,
}) {
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("#8f62ff");
  const [paidDefault, setPaidDefault] = useState(false);
  const [editing, setEditing] = useState(null); // type id or null
  const [confirmDel, setConfirmDel] = useState(null); // type or null

  const startEdit = (t) => {
    setEditing(t.id);
    setLabel(t.label);
    setColor(t.color || "#8f62ff");
    setPaidDefault(!!t.paidDefault);
  };

  const reset = () => {
    setEditing(null);
    setLabel("");
    setColor("#8f62ff");
    setPaidDefault(false);
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-content" style={{ width: 520, textAlign: "left" }}>
        <h3 style={{ marginTop: 0 }}>Manage activity types</h3>
        {isLoading && <Loader small label="Loading types..." />}
        {!isLoading && (
          <>
            <div style={{ display: "grid", gap: 12, margin: "12px 0 8px" }}>
              <label style={{ fontWeight: 600 }}>Label</label>
              <input
                type="text"
                placeholder="e.g. Inventory"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />

              <label style={{ fontWeight: 600, marginTop: 6 }}>Color</label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                />
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    background: color,
                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,.2)",
                  }}
                />
              </div>

              <label className="paid-check" style={{ marginTop: 6 }}>
                <input
                  type="checkbox"
                  checked={paidDefault}
                  onChange={(e) => setPaidDefault(e.target.checked)}
                />
                Paid by default
              </label>

              <div
                className="modal-actions"
                style={{ justifyContent: "flex-end" }}
              >
                <button
                  onClick={async () => {
                    if (!label.trim()) return;
                    if (editing) {
                      await onUpdate(editing, {
                        label: label.trim(),
                        color,
                        paidDefault,
                      });
                      toast.success("Type updated");
                    } else {
                      await onCreate({
                        label: label.trim(),
                        color,
                        paidDefault,
                      });
                      toast.success("Type created");
                    }
                    reset();
                  }}
                >
                  {editing ? "Save changes" : "Add type"}
                </button>
                <button
                  onClick={() => {
                    reset();
                    onClose();
                  }}
                  style={{ background: "#6b7280" }}
                >
                  Close
                </button>
              </div>
            </div>

            <hr style={{ margin: "12px 0" }} />

            <div style={{ maxHeight: 260, overflow: "auto" }}>
              {types.map((t) => (
                <div
                  key={t.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto auto",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 0",
                    borderBottom: "1px solid #eef2f6",
                  }}
                >
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      background: t.color || "#d1d5db",
                      boxShadow: "inset 0 0 0 1px rgba(0,0,0,.15)",
                    }}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t.label}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      {t.paidDefault ? "Paid" : "Unpaid"} •{" "}
                      {t.color || "no color"}
                    </div>
                  </div>
                  <button
                    onClick={() => startEdit(t)}
                    style={{ padding: "6px 10px" }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setConfirmDel(t)}
                    style={{ padding: "6px 10px", background: "#b91c1c" }}
                  >
                    Delete
                  </button>
                  {confirmDel && (
                    <ConfirmDialog
                      open={!!confirmDel}
                      title={`Delete "${confirmDel.label}"?`}
                      body="This removes the type from your company. Existing schedules keep their stored labels."
                      danger
                      confirmText="Delete"
                      onConfirm={async () => {
                        await onDelete(confirmDel.id);
                        setConfirmDel(null);
                        toast.success("Type deleted");
                      }}
                      onCancel={() => setConfirmDel(null)}
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PageModal({
  open,
  title,
  onClose,
  children,
  maxWidth = 1100,

  // Optional sticky header actions
  showHeaderActions = false,
  primaryActionText = "Save",
  onPrimaryAction,
  primaryActionDisabled = false,
}) {
  if (!open) return null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div
        className={`modal-content ${
          showHeaderActions ? "page-modal-with-actions" : ""
        }`}
        style={{ maxWidth, width: "min(1100px, 94vw)" }}
      >
        <div
          className={
            showHeaderActions
              ? "page-modal-sticky-header"
              : "page-modal-standard-header"
          }
        >
          <h3 className="page-modal-title">{title}</h3>

          {showHeaderActions ? (
            <div className="page-modal-header-actions">
              <button
                type="button"
                className="page-modal-cancel-btn"
                onClick={onClose}
                disabled={primaryActionDisabled}
              >
                Cancel
              </button>

              <button
                type="button"
                className="page-modal-primary-btn"
                onClick={onPrimaryAction}
                disabled={primaryActionDisabled}
              >
                {primaryActionText}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="timesheetbtn ghost"
            >
              ✕
            </button>
          )}
        </div>

        {children}
      </div>
    </div>
  );
}

export default function SchedulingEmployer() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [existingSchedules, setExistingSchedules] = useState([]);
  const [authChecking, setAuthChecking] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [weeklyOverviewStart, setWeeklyOverviewStart] = useState(null);
  const [weeklyOverviewData, setWeeklyOverviewData] = useState([]);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [activityTypes, setActivityTypes] = useState([]); // company-level types
  const [showTypesModal, setShowTypesModal] = useState(false);
  const [isTypesLoading, setIsTypesLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isOverviewLoading, setIsOverviewLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [workHoursByEmpDate, setWorkHoursByEmpDate] = useState({}); // { [empId]: { [ISODate]: number } }
  const [hasEntryByEmpDate, setHasEntryByEmpDate] = useState({}); // { [empId]: { [ISODate]: true } }
  const [multiAssign, setMultiAssign] = useState(false);
  // { [employeeId]: { [ISODate]: { isPaid: boolean, leaveType: string, reason?: string } } }
  const [weeklyLeaveMap, setWeeklyLeaveMap] = useState({});
  const [locations, setLocations] = useState([]);
  // ✅ Multi-location support (company-level)
  const [multiLocationEnabled, setMultiLocationEnabled] = useState(false);

  const [primaryLocation, setPrimaryLocation] = useState(null); // { id, name }

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // First-run guide (force settings first)
  const [schedBannerOpen, setSchedBannerOpen] = useState(false);

  const [confirm, setConfirm] = useState({
    open: false,
    title: "",
    body: "",
    danger: false,
    onConfirm: null,
  });
  const pendingContinue = useRef(null);

  const [limitsWarning, setLimitsWarning] = useState({
    open: false,
    lines: [],
  });
  const [proceedingAfterWarning, setProceedingAfterWarning] = useState(false);

  // ── Hours limits UI + helpers ───────────────────────────────────────────────
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [limitSettings, setLimitSettings] = useState({
    enforce: true, // true = block saves; false = warn/allow override
    companyWeekCap: 0, // hours (0 = unlimited)
    companyMonthCap: 0, // hours
    perEmpWeekCap: 0, // hours
    perEmpMonthCap: 0, // hours
  });

  const hoursFromShift = (shift) => {
    if (!shift || shift.isOffDay || shift.isHoliday || shift.isCallOut)
      return 0;

    const sMin = toMin(shift.startTime);
    const eMin = toMin(shift.stopTime);
    const base = Math.max(0, eMin > sMin ? eMin - sMin : 1440 - sMin + eMin); // overnight aware
    const acts = shift.activities || [];
    let delta = 0;
    for (const a of acts) {
      const dur = Math.max(
        0,
        (toMin(a.end) <= toMin(a.start) ? toMin(a.end) + 1440 : toMin(a.end)) -
          toMin(a.start),
      );

      delta += a.paid ? dur : -dur;
    }
    return Math.max(0, base + delta) / 60;
  };

  const editCameFromHistory = useRef(false);

  const sumScheduleHoursInRange = (schedule, startISO, endISO) => {
    const s = DateTime.fromISO(startISO, { zone: companyTimeZone }).startOf(
      "day",
    );
    const e = DateTime.fromISO(endISO, { zone: companyTimeZone }).endOf("day");
    const daySet = new Set();
    for (let i = 0; i < 400; i++) {
      const d = s.plus({ days: i });
      if (d > e) break;
      daySet.add(d.toISODate());
    }
    return (schedule.shifts || []).reduce((acc, sh) => {
      if (daySet.has(sh.date)) acc += hoursFromShift(sh);
      return acc;
    }, 0);
  };

  const openHoursSettings = useCallback(() => {
    setLimitsOpen(true);
  }, []);

  const periodBounds = (weekStartJSDate) => {
    const ws = DateTime.fromJSDate(weekStartJSDate, {
      zone: companyTimeZone,
    }).startOf("day");
    const we = ws.plus({ days: 6 }).endOf("day");
    const ms = ws.startOf("month");
    const me = ws.endOf("month");
    return {
      week: [ws.toISODate(), we.toISODate()],
      month: [ms.toISODate(), me.toISODate()],
    };
  };

  const [shifts, setShifts] = useState(
    [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ].map((day) => ({
      day,
      startTime: "08:00",
      stopTime: "17:00",
      isOffDay: false,
      isHoliday: false,
      activities: [],
      locationId: null,
      locationName: "",
    })),
  );

  const toMin = (hhmm = "00:00") => {
    const [h, m] = (hhmm || "00:00").split(":").map(Number);
    return h * 60 + m;
  };

  const parseLocalDate = useCallback((isoDateString) => {
    if (
      !isoDateString ||
      typeof isoDateString !== "string" ||
      !isoDateString.match(/^\d{4}-\d{2}-\d{2}$/)
    ) {
      console.warn("Invalid or missing ISO date string:", isoDateString);
      return new Date(); // fallback to today
    }

    const [year, month, day] = isoDateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  }, []);

  const formatDate = (isoString) => {
    return DateTime.fromISO(isoString, { zone: companyTimeZone }).toFormat(
      "LLL dd, yyyy",
    );
  };

  const [startDate, endDate] = dateRange;
  const showWorksiteDropdown =
    multiLocationEnabled && (locations?.length || 0) > 1;

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const employerRef = doc(db, "users", user.uid);
          const employerDoc = await getDoc(employerRef);
          const companyId = employerDoc.data().companyId;
          setCompanyId(companyId);

          const companySnap = await getDoc(doc(db, "companies", companyId));
          const companyData = companySnap.data() || {};

          setCompanyTimeZone(companyData.timeZone || "UTC");

          // ✅ NEW: multi-location flags
          setMultiLocationEnabled(!!companyData.multiLocationEnabled);

          // Load scheduling limits (company-level)
          try {
            const limitsRef = doc(
              db,
              `companies/${companyId}/settings/scheduling_limits`,
            );
            const limitsSnap = await getDoc(limitsRef);
            if (limitsSnap.exists()) {
              const d = limitsSnap.data() || {};
              setLimitSettings({
                enforce: d.enforce !== false,
                companyWeekCap: Number(d.companyWeekCap || 0),
                companyMonthCap: Number(d.companyMonthCap || 0),
                perEmpWeekCap: Number(d.perEmpWeekCap || 0),
                perEmpMonthCap: Number(d.perEmpMonthCap || 0),
              });
            }
          } catch (e) {
            console.warn("Failed to load scheduling limits:", e?.message);
          }

          const employeesRef = collection(
            db,
            `companies/${companyId}/employees`,
          );
          const snapshot = await getDocs(employeesRef);
          setEmployees(
            snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          );
          // ✅ Load company locations (worksites)
          try {
            const locRef = collection(db, `companies/${companyId}/locations`);
            const locSnap = await getDocs(locRef);
            const locs = locSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setLocations(locs);

            // ✅ NEW: determine primary location object
            const primary =
              locs.find(
                (l) => l.id === (companyData.primaryLocationId || null),
              ) ||
              locs.find((l) => l.isPrimary) ||
              locs[0] ||
              null;

            setPrimaryLocation(
              primary
                ? {
                    id: primary.id,
                    name: primary.name || primary.locationName || "Main Office",
                  }
                : { id: "PRIMARY", name: "Main Office" },
            );
          } catch (e) {
            console.warn("Failed to load locations:", e?.message);
            setLocations([]);
            setPrimaryLocation({ id: "PRIMARY", name: "Main Office" });
          }
        } catch (err) {
          console.error(err);
          toast.error(err.message);
        } finally {
          setLoading(false);
          setAuthChecking(false);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Load or seed company activity types
  useEffect(() => {
    if (!companyId) return;
    const load = async () => {
      setIsTypesLoading(true);
      try {
        const colRef = collection(db, `companies/${companyId}/activity_types`);
        const snap = await getDocs(colRef);
        if (snap.empty) {
          const defaults = [
            { label: "Break", paidDefault: false, color: "#7a869a" },
            { label: "Meeting", paidDefault: true, color: "#3d8bfd" },
            { label: "Training", paidDefault: true, color: "#22b573" },
            { label: "Other", paidDefault: false, color: "#8f62ff" },
          ];
          await Promise.all(
            defaults.map((d) =>
              addDoc(colRef, {
                ...d,
                createdAt: DateTime.now().toISO(),
                createdBy: auth.currentUser?.uid || null,
              }),
            ),
          );
          const seeded = await getDocs(colRef);
          setActivityTypes(seeded.docs.map((d) => ({ id: d.id, ...d.data() })));
        } else {
          setActivityTypes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } finally {
        setIsTypesLoading(false);
      }
    };
    load();
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const configured = await getSchedulingConfigured(db, companyId);
        if (!configured) {
          // Soft banner + open once (closable)
          setSchedBannerOpen(true);
          openHoursSettings();
        }
      } catch (e) {
        console.warn("Scheduling first-run check failed:", e);
      }
    })();
  }, [companyId, openHoursSettings]);

  // Create with details (used by the modal)
  const createActivityType = async ({
    label,
    color = "#8f62ff",
    paidDefault = false,
  }) => {
    const clean = (label || "").trim();
    if (!clean) return null;
    const dupe = activityTypes.find(
      (t) => t.label.toLowerCase() === clean.toLowerCase(),
    );
    if (dupe) return dupe;

    const colRef = collection(db, `companies/${companyId}/activity_types`);
    const ref = await addDoc(colRef, {
      label: clean,
      color,
      paidDefault,
      createdAt: DateTime.now().toISO(),
      createdBy: auth.currentUser?.uid || null,
    });
    const newType = { id: ref.id, label: clean, color, paidDefault };
    setActivityTypes((prev) => [...prev, newType]);
    return newType;
  };

  // Update
  const updateActivityType = async (id, patch) => {
    await updateDoc(
      doc(db, `companies/${companyId}/activity_types`, id),
      patch,
    );
    setActivityTypes((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    );
  };

  // Delete
  const deleteActivityType = async (id) => {
    await deleteDoc(doc(db, `companies/${companyId}/activity_types`, id));
    setActivityTypes((prev) => prev.filter((t) => t.id !== id));
  };

  // Save/merge the company limits settings
  const saveLimitSettings = async (patch) => {
    if (!companyId) return;
    setSavingLimits(true);
    try {
      const payload = {
        ...limitSettings,
        ...patch,
        updatedAt: serverTimestamp(),
      };
      await setDoc(
        doc(db, `companies/${companyId}/settings/scheduling_limits`),
        payload,
        { merge: true },
      );
      setLimitSettings(payload);

      // Mark onboarding complete and hide the banner (if showing)
      try {
        await setSchedulingConfigured(db, companyId);
      } catch {}

      setSchedBannerOpen(false);
      setLimitsOpen(false);
      toast.success("Hours limits saved.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to save hours limits.");
    } finally {
      setSavingLimits(false);
    }
  };

  // ✅ NEW: Refresh Schedule History list for the currently selected employee
  const refreshScheduleHistory = useCallback(
    async (employeeId) => {
      if (!companyId) return;

      if (!employeeId) {
        setExistingSchedules([]);
        return;
      }

      setIsHistoryLoading(true);
      try {
        const q = query(
          collection(db, `companies/${companyId}/schedules`),
          where("employeeId", "==", employeeId),
        );

        const snapshot = await getDocs(q);

        const schedules = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            if (!a.weekStartDate) return 1;
            if (!b.weekStartDate) return -1;
            return b.weekStartDate.localeCompare(a.weekStartDate);
          });

        setExistingSchedules(schedules);
      } finally {
        setIsHistoryLoading(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    refreshScheduleHistory(selectedEmployee);
  }, [selectedEmployee, refreshScheduleHistory]);

  const requestDeleteSchedule = (id) => {
    setConfirm({
      open: true,
      title: "Delete this schedule?",
      body: "This permanently removes the schedule for that week.",
      danger: true,
      onConfirm: async () => {
        setConfirm((c) => ({ ...c, open: false }));
        setDeletingId(id);
        try {
          const companyId = (
            await getDoc(doc(db, "users", auth.currentUser.uid))
          ).data().companyId;
          await deleteDoc(doc(db, `companies/${companyId}/schedules`, id));
          setExistingSchedules((prev) => prev.filter((s) => s.id !== id));
          toast.success("Schedule deleted.");
        } catch (e) {
          toast.error(e.message || "Failed to delete schedule.");
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const handleShiftChange = (day, field, value) => {
    setShifts((prev) =>
      prev.map((shift) => {
        if (shift.day !== day) return shift;

        let next = { ...shift, [field]: value };

        // If Off Day or Holiday turned on, clear activities
        if ((field === "isOffDay" || field === "isHoliday") && value) {
          next.activities = [];
        }

        // If shift window changed, keep only valid activities inside it
        const S = toMin(next.startTime);
        const E = toMin(next.stopTime);
        const overnight = E <= S;

        const acts = (next.activities || []).filter((a) => {
          const sRaw = toMin(a.start);
          const eRaw = toMin(a.end);

          // reject zero-length
          if (sRaw === eRaw) return false;

          // non-overnight: normal bounds
          if (!overnight) return sRaw >= S && eRaw <= E && sRaw < eRaw;

          // overnight: normalize into a timeline
          let s = sRaw;
          let e = eRaw;

          if (s < S) s += 1440;
          if (e < S) e += 1440;
          if (e <= s) e += 1440;

          const shiftEnd = E + 1440; // overnight end
          return S <= s && s < e && e <= shiftEnd;
        });

        return { ...next, activities: acts };
      }),
    );
  };

  const addActivity = (day) => {
    const def = activityTypes[0] || {
      id: null,
      label: "Break",
      paidDefault: false,
    };
    setShifts((s) =>
      s.map((d) =>
        d.day === day
          ? {
              ...d,
              activities: [
                ...(d.activities || []),
                {
                  id: crypto.randomUUID(),
                  type: def.label, // keep legacy string for compatibility
                  typeId: def.id, // new: reference to company type
                  start: "12:00",
                  end: "12:30",
                  paid: !!def.paidDefault,
                  note: "",
                },
              ],
            }
          : d,
      ),
    );
  };

  const updateActivity = (day, id, field, value) => {
    setShifts((s) =>
      s.map((d) =>
        d.day === day
          ? {
              ...d,
              activities: (d.activities || []).map((a) =>
                a.id === id ? { ...a, [field]: value } : a,
              ),
            }
          : d,
      ),
    );
  };

  const removeActivity = (day, id) => {
    setShifts((s) =>
      s.map((d) =>
        d.day === day
          ? {
              ...d,
              activities: (d.activities || []).filter((a) => a.id !== id),
            }
          : d,
      ),
    );
  };

  // Load approved leave for a given week into weeklyLeaveMap
  // Load approved leave for a given week into weeklyLeaveMap
  const loadWeeklyLeaveMap = useCallback(
    async (weekStartDate) => {
      if (!companyId || !weekStartDate) return;

      const weekStart = DateTime.fromJSDate(weekStartDate, {
        zone: companyTimeZone,
      }).startOf("day");
      const weekEnd = weekStart.plus({ days: 6 }).endOf("day");

      try {
        const leaveCol = collection(db, `companies/${companyId}/leave_history`);

        // 🔥 Only filter by status in Firestore (no index problems)
        const leaveQ = query(leaveCol, where("status", "==", "Approved"));
        const leaveSnap = await getDocs(leaveQ);

        const map = {}; // { empId: { dateISO: { isPaid, leaveType, reason } } }

        leaveSnap.forEach((docSnap) => {
          const row = docSnap.data();
          const empId = row.employeeId || row.userId;
          if (!empId) return;

          // Handle string OR Timestamp for dates
          const rawStart = row.startDate;
          const rawEnd = row.endDate ?? row.startDate;

          const toLuxon = (v, endOfDay = false) => {
            if (!v) return null;
            if (typeof v === "string") {
              return endOfDay
                ? DateTime.fromISO(v, { zone: companyTimeZone }).endOf("day")
                : DateTime.fromISO(v, { zone: companyTimeZone }).startOf("day");
            }
            const asDate = v.toDate ? v.toDate() : v;
            return endOfDay
              ? DateTime.fromJSDate(asDate, { zone: companyTimeZone }).endOf(
                  "day",
                )
              : DateTime.fromJSDate(asDate, { zone: companyTimeZone }).startOf(
                  "day",
                );
          };

          const start = toLuxon(rawStart, false);
          const end = toLuxon(rawEnd, true);
          if (!start || !end) return;

          // ❌ If no overlap with this week, skip
          if (end < weekStart || start > weekEnd) return;

          // Clamp to this week range
          let cur = start < weekStart ? weekStart : start;
          const last = end > weekEnd ? weekEnd : end;

          const isPaid =
            row.isPaid !== undefined
              ? !!row.isPaid
              : (row.leaveCategory || row.leaveType || "")
                  .toLowerCase()
                  .includes("unpaid")
              ? false
              : true;

          while (cur <= last) {
            const iso = cur.toISODate();
            if (!map[empId]) map[empId] = {};
            const timeOfDay =
              row.timeOfDay ||
              row.leaveTimeOfDay ||
              row.partOfDay ||
              row.durationType || // fallback if you used a different field name
              "";

            const customStart =
              row.customStart ||
              row.startTime || // fallback if you used startTime
              row.halfDayStart || // fallback
              row.customStartTime || // fallback
              "";

            const customEnd =
              row.customEnd ||
              row.endTime || // fallback if you used endTime
              row.halfDayEnd || // fallback
              row.customEndTime || // fallback
              "";

            // normalize "13:00 PM" etc if you accidentally saved weird strings
            const clean = (t) => (typeof t === "string" ? t.trim() : "");

            map[empId][iso] = {
              isPaid,
              leaveType: row.leaveCategory || row.leaveType || "",
              reason: row.reason || "",

              // ✅ ADD THESE (your UI needs them)
              timeOfDay: String(timeOfDay || ""),
              customStart: clean(customStart),
              customEnd: clean(customEnd),
            };

            cur = cur.plus({ days: 1 });
          }
        });

        setWeeklyLeaveMap(map);
      } catch (e) {
        console.warn("Failed to load weekly leave map:", e);
      }
    },
    [companyId, companyTimeZone],
  );

  useEffect(() => {
    if (!startDate || !endDate || !companyTimeZone) return;
    // When creating a schedule, keep leave map in sync with the selected week

    // If we are editing an existing schedule, don't reseed the dates
    if (editingScheduleId) return;

    const start = new Date(startDate);

    const newShifts = Array.from({ length: 7 }).map((_, i) => {
      const dayDate = new Date(start.getTime());
      dayDate.setDate(start.getDate() + i);

      const luxonDate = DateTime.fromJSDate(dayDate, {
        zone: companyTimeZone,
      });

      return {
        day: luxonDate.toFormat("cccc"),
        date: luxonDate.toISODate(),
        startTime: "08:00",
        stopTime: "17:00",
        isOffDay: false,
        isHoliday: false,
        locationId: showWorksiteDropdown
          ? null
          : primaryLocation?.id || "PRIMARY",
        locationName: showWorksiteDropdown
          ? ""
          : primaryLocation?.name || "Main Office",
      };
    });

    // keep any existing activities aligned by weekday
    setShifts((prev) => {
      const byDay = Object.fromEntries(prev.map((p) => [p.day, p]));
      return newShifts.map((ns) => ({
        ...ns,
        activities: byDay[ns.day]?.activities || [],
      }));
    });
  }, [
    startDate,
    endDate,
    companyTimeZone,
    editingScheduleId,
    showWorksiteDropdown,
    primaryLocation,
  ]);

  // When creating a schedule, keep leave map in sync with the selected week
  useEffect(() => {
    if (!companyId || !startDate || !endDate) return;

    // ✅ don’t overwrite leave map from edit panel week;
    // weekly overview will manage its own leave loads via fetchWeeklyOverview()
    if (editingScheduleId) return;

    loadWeeklyLeaveMap(startDate);
  }, [companyId, startDate, endDate, editingScheduleId, loadWeeklyLeaveMap]);

  const handleSaveSchedule = async () => {
    setIsSaving(true);
    try {
      if (!startDate || !endDate) {
        throw new Error("Please select a full week (7 days).");
      }

      const newStart = new Date(startDate);
      const newEnd = new Date(endDate);
      newStart.setHours(0, 0, 0, 0);
      newEnd.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // NEW: only block *new* schedules that are completely in the past
      if (!editingScheduleId && newEnd < today) {
        throw new Error(
          "You cannot create a schedule that is entirely in the past.",
        );
      }

      // company id
      const employerRef = doc(db, "users", auth.currentUser.uid);
      const employerDoc = await getDoc(employerRef);
      const companyId = employerDoc.data().companyId;

      const weekStart = DateTime.fromJSDate(newStart, {
        zone: companyTimeZone,
      }).toISODate();
      const weekEnd = DateTime.fromJSDate(newEnd, {
        zone: companyTimeZone,
      }).toISODate();

      // normalize+validate activities
      const adjustedShifts = shifts.map((shift) => ({
        ...shift,
        date: DateTime.fromISO(shift.date || weekStart, {
          zone: companyTimeZone,
        }).toISODate(),
        activities: (shift.activities || []).map((a) => ({ ...a })),
      }));

      for (const d of adjustedShifts) {
        if (!d || d.isOffDay || d.isHoliday) continue;
        const S = toMin(d.startTime);
        const E = toMin(d.stopTime);
        const acts = d.activities || [];
        for (const a of acts) {
          const s = toMin(a.start),
            e = toMin(a.end);
          const overnight = E <= S;

          const ok = !overnight
            ? S <= s && e <= E && s < e
            : // overnight: activity must be in either side of the split window
              (s >= S || e <= E) && s !== e;

          if (!ok) {
            throw new Error(
              `Invalid activity on ${d.day}: ${a.type ||
                "Activity"} must be within ${d.startTime}–${d.stopTime}`,
            );
          }
        }
        // ✅ OVERNIGHT-SAFE ACTIVITY OVERLAP CHECK
        // ✅ HARD guardrail: require worksite on working days
        // ✅ Worksite rules:
        // - If multi-location dropdown is shown, require selection
        // - Otherwise auto-fill primary location
        // ✅ Check approved leave for this day
        const leaveInfo =
          !multiAssign && selectedEmployee
            ? weeklyLeaveMap?.[selectedEmployee]?.[d.date] || null
            : null;

        // half-day detection (matches your overview logic)
        const isHalfDay =
          !!leaveInfo &&
          (String(leaveInfo.timeOfDay || "")
            .toLowerCase()
            .includes("half") ||
            String(leaveInfo.timeOfDay || "")
              .toLowerCase()
              .includes("am") ||
            String(leaveInfo.timeOfDay || "")
              .toLowerCase()
              .includes("pm") ||
            !!(leaveInfo.customStart && leaveInfo.customEnd));

        // full-day leave = approved leave AND not half-day
        const isFullDayLeave = !!leaveInfo && !isHalfDay;

        // ✅ Only enforce worksite if NOT full-day leave
        if (!isFullDayLeave) {
          if (showWorksiteDropdown) {
            if (!d.locationId) {
              throw new Error(`Select a worksite for ${d.day} (${d.date}).`);
            }
          } else {
            d.locationId = primaryLocation?.id || "PRIMARY";
            d.locationName = primaryLocation?.name || "Main Office";
          }
        }

        const overnight2 = E <= S;
        const shiftEnd = overnight2 ? E + 1440 : E;

        // normalize a time into the shift timeline
        const norm = (hhmm) => {
          let m = toMin(hhmm);
          if (overnight2 && m < S) m += 1440; // early-morning belongs to next day
          return m;
        };

        const intervals = (acts || []).map((a) => {
          let s = norm(a.start);
          let e = norm(a.end);

          // allow activity to cross midnight
          if (e <= s) e += 1440;

          // validate bounds
          if (!(S <= s && s < e && e <= shiftEnd)) {
            throw new Error(
              `Invalid activity on ${d.day}: ${a.type ||
                "Activity"} must be within ${d.startTime}–${d.stopTime}`,
            );
          }

          return { s, e };
        });

        // overlap detection
        intervals.sort((a, b) => a.s - b.s);
        for (let i = 1; i < intervals.length; i++) {
          if (intervals[i].s < intervals[i - 1].e) {
            throw new Error(`Activities overlap on ${d.day}.`);
          }
        }
      }

      // helper: payload
      const buildPayload = (emp) => ({
        employeeId: emp.id,
        employeeName: emp.name,
        weekStartDate: weekStart,
        weekEndDate: weekEnd,
        shifts: adjustedShifts,
        repeatWeekly,
        assignedBy: auth.currentUser?.uid || null,
        createdAt: DateTime.now()
          .setZone(companyTimeZone)
          .toISO(),
      });

      const doActualSave = async () => {
        // EDIT
        if (editingScheduleId) {
          const emp = employees.find((e) => e.id === selectedEmployee);
          if (!emp || emp.status !== "Active")
            throw new Error(
              "You can only update schedules for Active employees.",
            );

          const q1 = query(
            collection(db, `companies/${companyId}/schedules`),
            where("employeeId", "==", emp.id),
          );
          const snap1 = await getDocs(q1);
          const overlapping = snap1.docs.find((d) => {
            const { weekStartDate, weekEndDate } = d.data();

            const existingStart = parseLocalDate(weekStartDate);
            const existingEnd = parseLocalDate(weekEndDate);
            existingStart.setHours(0, 0, 0, 0);
            existingEnd.setHours(0, 0, 0, 0);

            return (
              newStart <= existingEnd &&
              newEnd >= existingStart &&
              d.id !== editingScheduleId
            );
          });
          if (overlapping) {
            const { weekStartDate, weekEndDate } = overlapping.data();
            throw new Error(
              `This employee already has a schedule from ${formatDate(
                weekStartDate,
              )} to ${formatDate(weekEndDate)}.`,
            );
          }

          await updateDoc(
            doc(db, `companies/${companyId}/schedules`, editingScheduleId),
            buildPayload(emp),
          );
          await refreshScheduleHistory(selectedEmployee);

          // ✅ INSERT RIGHT HERE
          await sendUserNotification(
            emp.id,
            `scheduleUpdated_${emp.id}_${weekStart}`,
            {
              type: "scheduleUpdated",
              title: "Schedule updated",
              message: `Your schedule for ${weekStart} – ${weekEnd} was updated. Please review.`,
              companyId,
              scheduleId: editingScheduleId,
              weekStartDate: weekStart,
              weekEndDate: weekEnd,
            },
          );

          toast.success("Schedule updated successfully!");
          // ✅ refresh weekly overview data + leave map so Paid leave displays immediately
          await refreshOverviewAfterSave(newStart);
          // close slide-over
          setShowEditPanel(false);
          setShowConfirmModal(false);

          // ✅ if we came from history, keep the employee selected
          if (editCameFromHistory.current) {
            resetForm({ keepEmployee: true }); // <-- key line
            setHistoryOpen(true); // keep / ensure open
            editCameFromHistory.current = false;
          } else {
            resetForm();
          }

          return;
        }

        // SINGLE CREATE
        if (!multiAssign) {
          if (!selectedEmployee) throw new Error("Select an employee.");
          const emp = employees.find((e) => e.id === selectedEmployee);
          if (!emp || emp.status !== "Active")
            throw new Error(
              "You can only create schedules for Active employees.",
            );

          const q2 = query(
            collection(db, `companies/${companyId}/schedules`),
            where("employeeId", "==", emp.id),
          );
          const snap2 = await getDocs(q2);
          const overlapping = snap2.docs.find((d) => {
            const { weekStartDate, weekEndDate } = d.data();

            const existingStart = parseLocalDate(weekStartDate);
            const existingEnd = parseLocalDate(weekEndDate);
            existingStart.setHours(0, 0, 0, 0);
            existingEnd.setHours(0, 0, 0, 0);

            return newStart <= existingEnd && newEnd >= existingStart;
          });

          if (overlapping) {
            const { weekStartDate, weekEndDate } = overlapping.data();
            throw new Error(
              `This employee already has a schedule from ${formatDate(
                weekStartDate,
              )} to ${formatDate(weekEndDate)}.`,
            );
          }

          const newRef = await addDoc(
            collection(db, `companies/${companyId}/schedules`),
            buildPayload(emp),
          );
          // ✅ INSERT RIGHT HERE (after newRef is created)
          await sendUserNotification(
            emp.id,
            `scheduleCreated_${emp.id}_${weekStart}`,
            {
              type: "scheduleCreated",
              title: "New schedule posted",
              message: `Your schedule for ${weekStart} – ${weekEnd} is now available.`,
              companyId,
              scheduleId: newRef.id,
              weekStartDate: weekStart,
              weekEndDate: weekEnd,
            },
          );

          await updateDoc(doc(db, "companies", companyId), {
            "quickSetupProgress.createdSchedule": true,
          });
          toast.success(`Schedule created successfully for ${emp.name}`);
          setExistingSchedules((prev) => [
            ...prev,
            { id: newRef.id, ...buildPayload(emp) },
          ]);
          // ✅ refresh weekly overview immediately (shows new schedule without page refresh)
          await refreshOverviewAfterSave(new Date(newStart));

          resetForm();
          setShowConfirmModal(false);
          return;
        }

        // MULTI CREATE
        if (selectedEmployees.length === 0)
          throw new Error("Select at least one employee.");
        const targets = employees.filter(
          (e) => selectedEmployees.includes(e.id) && e.status === "Active",
        );
        if (targets.length === 0)
          throw new Error("No Active employees selected.");

        const chunk = (arr, size) =>
          Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
            arr.slice(i * size, i * size + size),
          );
        const toCreate = [];
        const skipped = [];

        for (const group of chunk(targets, 10)) {
          for (const emp of group) {
            const qEmp = query(
              collection(db, `companies/${companyId}/schedules`),
              where("employeeId", "==", emp.id),
            );
            const snap = await getDocs(qEmp);
            const hasOverlap = snap.docs.some((d) => {
              const { weekStartDate, weekEndDate } = d.data();

              const existingStart = parseLocalDate(weekStartDate);
              const existingEnd = parseLocalDate(weekEndDate);
              existingStart.setHours(0, 0, 0, 0);
              existingEnd.setHours(0, 0, 0, 0);

              return newStart <= existingEnd && newEnd >= existingStart;
            });

            if (hasOverlap) skipped.push(emp);
            else toCreate.push(emp);
          }
        }

        if (toCreate.length === 0) {
          if (skipped.length) {
            toast.error(
              `No schedules created. ${skipped.length} selected ${
                skipped.length === 1 ? "employee has" : "employees have"
              } an overlapping schedule for this week.`,
            );
          } else {
            toast.error("No schedules created.");
          }
          return;
        }

        const batch = writeBatch(db);
        const col = collection(db, `companies/${companyId}/schedules`);
        for (const emp of toCreate) {
          batch.set(doc(col), buildPayload(emp));
        }
        batch.update(doc(db, "companies", companyId), {
          "quickSetupProgress.createdSchedule": true,
        });
        await batch.commit();

        // ✅ INSERT RIGHT HERE (after batch.commit)
        await Promise.all(
          toCreate.map((emp) =>
            sendUserNotification(
              emp.id,
              `scheduleCreated_${emp.id}_${weekStart}`,
              {
                type: "scheduleCreated",
                title: "New schedule posted",
                message: `Your schedule for ${weekStart} – ${weekEnd} is now available.`,
                companyId,
                weekStartDate: weekStart,
                weekEndDate: weekEnd,
              },
            ),
          ),
        );

        toast.success(
          `Created ${toCreate.length} schedule${
            toCreate.length > 1 ? "s" : ""
          }${skipped.length ? ` • Skipped ${skipped.length} overlap(s)` : ""}`,
        );
        resetForm();
        setShowConfirmModal(false);
      };

      // ===== caps check =====
      const {
        week: [weekStartISO, weekEndISO],
        month: [monthStartISO, monthEndISO],
      } = periodBounds(newStart);
      const hoursThisSchedule = adjustedShifts.reduce(
        (acc, sh) => acc + hoursFromShift(sh),
        0,
      );

      const targetsForCheck = editingScheduleId
        ? [employees.find((e) => e.id === selectedEmployee)].filter(Boolean)
        : !multiAssign
        ? [employees.find((e) => e.id === selectedEmployee)].filter(Boolean)
        : employees.filter((e) => selectedEmployees.includes(e.id));

      if (targetsForCheck.length === 0)
        throw new Error("Select at least one employee.");

      const sumExistingHours = async (
        rangeStartISO,
        rangeEndISO,
        employeeId = null,
      ) => {
        const snap = await getDocs(
          query(collection(db, `companies/${companyId}/schedules`)),
        );
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((s) => {
            if (employeeId && s.employeeId !== employeeId) return false;
            const sStart = parseLocalDate(s.weekStartDate);
            const sEnd = parseLocalDate(s.weekEndDate);
            return (
              sStart <= new Date(weekEndISO) && sEnd >= new Date(weekStartISO)
            );
          });
        return rows.reduce(
          (sum, sched) =>
            sum + sumScheduleHoursInRange(sched, rangeStartISO, rangeEndISO),
          0,
        );
      };

      let companyWeekSoFar = 0,
        companyMonthSoFar = 0;
      if (limitSettings.companyWeekCap > 0)
        companyWeekSoFar = await sumExistingHours(
          weekStartISO,
          weekEndISO,
          null,
        );
      if (limitSettings.companyMonthCap > 0)
        companyMonthSoFar = await sumExistingHours(
          monthStartISO,
          monthEndISO,
          null,
        );

      const addCompanyWeek = hoursThisSchedule * targetsForCheck.length;
      const addCompanyMonth = hoursThisSchedule * targetsForCheck.length;

      const violations = [];
      const h2 = (n) => Number(n || 0).toFixed(2);

      if (
        limitSettings.companyWeekCap > 0 &&
        companyWeekSoFar + addCompanyWeek > limitSettings.companyWeekCap
      ) {
        violations.push(
          `Company weekly cap exceeded: ${h2(
            companyWeekSoFar + addCompanyWeek,
          )}/${h2(limitSettings.companyWeekCap)} hrs`,
        );
      }
      if (
        limitSettings.companyMonthCap > 0 &&
        companyMonthSoFar + addCompanyMonth > limitSettings.companyMonthCap
      ) {
        violations.push(
          `Company monthly cap exceeded: ${h2(
            companyMonthSoFar + addCompanyMonth,
          )}/${h2(limitSettings.companyMonthCap)} hrs`,
        );
      }

      for (const emp of targetsForCheck) {
        if (limitSettings.perEmpWeekCap > 0) {
          const empWeekSoFar = await sumExistingHours(
            weekStartISO,
            weekEndISO,
            emp.id,
          );
          if (empWeekSoFar + hoursThisSchedule > limitSettings.perEmpWeekCap) {
            violations.push(
              `${emp.name}: weekly cap exceeded (${h2(
                empWeekSoFar + hoursThisSchedule,
              )}/${h2(limitSettings.perEmpWeekCap)} hrs)`,
            );
          }
        }
        if (limitSettings.perEmpMonthCap > 0) {
          const empMonthSoFar = await sumExistingHours(
            monthStartISO,
            monthEndISO,
            emp.id,
          );
          if (
            empMonthSoFar + hoursThisSchedule >
            limitSettings.perEmpMonthCap
          ) {
            violations.push(
              `${emp.name}: monthly cap exceeded (${h2(
                empMonthSoFar + hoursThisSchedule,
              )}/${h2(limitSettings.perEmpMonthCap)} hrs)`,
            );
          }
        }
      }

      if (violations.length) {
        if (limitSettings.enforce) {
          throw new Error(violations.join(" • "));
        } else {
          setIsSaving(false); // pause spinner while modal is open
          setLimitsWarning({ open: true, lines: violations });

          pendingContinue.current = async () => {
            setProceedingAfterWarning(true);
            setLimitsWarning({ open: false, lines: [] });
            setIsSaving(true);
            try {
              await doActualSave();
            } finally {
              setProceedingAfterWarning(false);
              setIsSaving(false);
            }
          };
          return; // wait for user to click Proceed
        }
      }

      // no violations → save
      await doActualSave();

      return true; // ✅ SUCCESS
    } catch (err) {
      toast.error(err.message || "Failed to save schedules.");
      return false; // ✅ FAIL
    } finally {
      setIsSaving(false);
    }
  };

  const fetchWeeklyOverview = useCallback(
    async (weekStartDate) => {
      if (!companyId || !weekStartDate) return;
      setIsOverviewLoading(true);

      try {
        const weekStart = new Date(weekStartDate);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        // Also load leave info for this week
        await loadWeeklyLeaveMap(weekStart);

        // ---- 1) Schedules ----
        const q = query(collection(db, `companies/${companyId}/schedules`));
        const snapshot = await getDocs(q);

        const filtered = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((schedule) => {
            const start = parseLocalDate(schedule.weekStartDate);
            const end = parseLocalDate(schedule.weekEndDate);
            return start <= weekEnd && end >= weekStart;
          });

        // project recurring weeks when needed
        const key = (empId, weekISO) => `${empId}::${weekISO}`;
        const explicit = new Map();
        for (const s of filtered)
          explicit.set(key(s.employeeId, s.weekStartDate), s);

        for (const emp of employees) {
          const weekISO = DateTime.fromJSDate(weekStart, {
            zone: companyTimeZone,
          }).toISODate();

          const k = key(emp.id, weekISO);
          if (explicit.has(k)) continue;

          const prior = snapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter(
              (s) =>
                s.employeeId === emp.id &&
                s.weekStartDate <= weekStart.toISOString().split("T")[0],
            )
            .sort((a, b) => b.weekStartDate.localeCompare(a.weekStartDate))[0];

          if (prior && prior.repeatWeekly) {
            const baseStart = DateTime.fromISO(prior.weekStartDate, {
              zone: companyTimeZone,
            });

            const targetStart = DateTime.fromJSDate(weekStart, {
              zone: companyTimeZone,
            });

            const deltaDays = Math.round(
              targetStart.diff(baseStart, "days").days,
            );

            const shifts = (prior.shifts || []).map((s) => ({
              ...s,
              date: DateTime.fromISO(s.date, { zone: companyTimeZone })
                .plus({ days: deltaDays })
                .toISODate(),
            }));

            filtered.push({
              ...prior,
              weekStartDate: targetStart.toISODate(),
              weekEndDate: targetStart.plus({ days: 6 }).toISODate(),
              shifts,
              _fromRecurring: prior.weekStartDate,
            });
          }
        }

        setWeeklyOverviewData(filtered);

        // ---- 2) Worked hours from time_tracking for this week ----
        const weekStartISO = DateTime.fromJSDate(weekStart, {
          zone: companyTimeZone,
        }).toISODate();

        const weekEndISO = DateTime.fromJSDate(weekEnd, {
          zone: companyTimeZone,
        }).toISODate();

        const ttQ = query(
          collection(db, `companies/${companyId}/time_tracking`),
          where("date", ">=", weekStartISO),
          where("date", "<=", weekEndISO),
        );

        const ttSnap = await getDocs(ttQ);

        const hoursMap = {};
        const hasMap = {};

        ttSnap.forEach((d) => {
          const row = d.data();
          const empId = row.userId;
          const dateISO = row.date;
          const hrs = Number(row.hoursWorked || 0);

          if (!hoursMap[empId]) hoursMap[empId] = {};
          if (!hasMap[empId]) hasMap[empId] = {};

          hoursMap[empId][dateISO] = (hoursMap[empId][dateISO] || 0) + hrs;
          hasMap[empId][dateISO] = true;
        });

        setWorkHoursByEmpDate(hoursMap);
        setHasEntryByEmpDate(hasMap);
      } finally {
        setIsOverviewLoading(false);
      }
    },
    [companyId, companyTimeZone, employees, loadWeeklyLeaveMap, parseLocalDate],
  );

  const refreshOverviewAfterSave = useCallback(
    async (weekStartJS) => {
      // Keep the overview focused on the same week that was saved
      setWeeklyOverviewStart(weekStartJS);

      // Force immediate refresh of Weekly Overview + leave map
      await fetchWeeklyOverview(weekStartJS);
    },
    [fetchWeeklyOverview],
  );

  const EMPTY_WEEK = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ].map((day) => ({
    day,
    startTime: "08:00",
    stopTime: "17:00",
    isOffDay: false,
    isHoliday: false,
    activities: [],
    locationId: null,
    locationName: "",
  }));

  const resetForm = (opts = {}) => {
    const { keepEmployee = false } = opts;

    if (!keepEmployee) {
      setSelectedEmployee("");
    }

    setSelectedEmployees([]);
    setMultiAssign(false);
    setDateRange([null, null]);
    setRepeatWeekly(false);
    setEditingScheduleId(null);
    setShifts(EMPTY_WEEK); // no dates → next create will seed correctly
  };

  const handleEditSchedule = (schedule) => {
    setMultiAssign(false); // ensure edit is single-target
    setSelectedEmployees([]);
    setEditingScheduleId(schedule.id); // set first (helps effects know we’re editing)
    setSelectedEmployee(schedule.employeeId);
    setDateRange([
      parseLocalDate(schedule.weekStartDate),
      parseLocalDate(schedule.weekEndDate),
    ]);
    setShifts(schedule.shifts || []);

    setRepeatWeekly(schedule.repeatWeekly);
    setShowEditPanel(true);
  };

  if (authChecking) {
    return <Loader label={null} />;
  }
  if (!companyId)
    return <p style={{ color: "red" }}>Employer not authenticated.</p>;

  return (
    <div className="schedule-title-container">
      <div className="scheduling-wrapper">
        <div
          style={{
            marginBottom: 8,
          }}
        >
          {schedBannerOpen && (
            <div
              style={{
                padding: "10px 12px",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                background: "#f8fafc",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              role="status"
              aria-live="polite"
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span aria-hidden>🗓️</span>
                <div>
                  <strong>Complete Scheduling setup</strong>
                  <div style={{ color: "#64748b", fontSize: 13 }}>
                    Set caps (weekly/monthly &amp; per-employee). You can do it
                    later, too.
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="hoursbtn-secondary"
                  onClick={openHoursSettings}
                >
                  Open Hours Settings
                </button>
                <button
                  type="button"
                  className="timesheetbtn ghost"
                  onClick={() => setSchedBannerOpen(false)}
                  title="Hide reminder for now"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <Loader label="Loading employee data..." />
        ) : (
          <>
            {/* TOP BAR */}
            <div className="wo-topbar">
              <div>
                <h1 className="wo-title">Weekly Overview</h1>
                <p className="wo-subtitle">
                  Track scheduled vs. worked hours, spot missed shifts, and
                  review approved leave.
                </p>
              </div>

              <div className="wo-actions">
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => {
                    // open Create Schedule modal
                    setCreateOpen(true);

                    // reset edit mode (optional but recommended)
                    setEditingScheduleId(null);
                    setShowEditPanel(false);
                  }}
                >
                  + Create schedule
                </button>

                <button
                  type="button"
                  className="btn-ghost-history"
                  onClick={() => setHistoryOpen(true)}
                >
                  Schedule history
                </button>

                <button
                  type="button"
                  className="leave-settings-btn"
                  onClick={openHoursSettings}
                  aria-label="Open scheduling settings"
                  title="Scheduling settings"
                >
                  <Settings size={21} color="#2f4d66" />
                </button>
              </div>
            </div>

            {/* DEFAULT PAGE CONTENT = WEEKLY OVERVIEW */}
            <WeeklyOverviewSection
              employees={employees.filter((e) => e.status === "Active")}
              weeklyOverviewStart={weeklyOverviewStart}
              setWeeklyOverviewStart={setWeeklyOverviewStart}
              weeklyOverviewData={weeklyOverviewData}
              fetchWeeklyOverview={fetchWeeklyOverview}
              toMin={toMin}
              isOverviewLoading={isOverviewLoading}
              companyTimeZone={companyTimeZone}
              workHoursByEmpDate={workHoursByEmpDate}
              hasEntryByEmpDate={hasEntryByEmpDate}
              companyId={companyId}
              weeklyLeaveMap={weeklyLeaveMap}
            />

            {/* CREATE MODAL */}
            {/* CREATE MODAL */}
            <PageModal
              open={createOpen}
              title={editingScheduleId ? "Edit Schedule" : "Create Schedule"}
              onClose={() => {
                setCreateOpen(false);
                setShowConfirmModal(false);
              }}
              maxWidth={1200}
              showHeaderActions
              primaryActionText={
                editingScheduleId ? "Update Schedule" : "Publish Schedule"
              }
              onPrimaryAction={() => setShowConfirmModal(true)}
              primaryActionDisabled={isSaving}
            >
              <CreateScheduleSection
                employees={employees}
                selectedEmployee={selectedEmployee}
                setSelectedEmployee={setSelectedEmployee}
                selectedEmployees={selectedEmployees}
                setSelectedEmployees={setSelectedEmployees}
                multiAssign={multiAssign}
                setMultiAssign={setMultiAssign}
                startDate={startDate}
                endDate={endDate}
                dateRange={dateRange}
                setDateRange={setDateRange}
                repeatWeekly={repeatWeekly}
                setRepeatWeekly={setRepeatWeekly}
                shifts={shifts}
                setShifts={setShifts}
                handleShiftChange={handleShiftChange}
                addActivity={addActivity}
                updateActivity={updateActivity}
                removeActivity={removeActivity}
                parseLocalDate={parseLocalDate}
                showConfirmModal={showConfirmModal}
                setShowConfirmModal={setShowConfirmModal}
                editingScheduleId={editingScheduleId}
                handleSaveSchedule={async () => {
                  const ok = await handleSaveSchedule();
                  if (ok) setCreateOpen(false);
                }}
                activityTypes={activityTypes}
                onCreateActivityType={createActivityType}
                onOpenTypesModal={() => setShowTypesModal(true)}
                isSaving={isSaving}
                isTypesLoading={isTypesLoading}
                toMin={toMin}
                weeklyLeaveMap={weeklyLeaveMap}
                locations={locations}
                showWorksiteDropdown={showWorksiteDropdown}
                primaryLocation={primaryLocation}
              />
            </PageModal>

            {/* HISTORY MODAL */}
            <PageModal
              open={historyOpen}
              title="Schedule History"
              onClose={() => setHistoryOpen(false)}
              maxWidth={1100}
            >
              <ExistingScheduleSection
                employees={employees}
                selectedEmployee={selectedEmployee}
                setSelectedEmployee={setSelectedEmployee}
                existingSchedules={existingSchedules}
                handleEditSchedule={(schedule) => {
                  editCameFromHistory.current = true; // ✅ MARK SOURCE
                  handleEditSchedule(schedule); // loads schedule + opens slide-over
                  setCreateOpen(false); // just in case create modal is open
                  // ✅ DO NOT close history modal
                }}
                handleDeleteSchedule={requestDeleteSchedule}
                isHistoryLoading={isHistoryLoading}
                deletingId={deletingId}
                Loader={Loader}
                companyTimeZone={companyTimeZone}
              />
            </PageModal>
          </>
        )}

        {showEditPanel && (
          <div className="edit-slide-over">
            <div className="edit-panel edit-schedule-panel">
              {/* STICKY EDIT HEADER */}
              <div className="edit-schedule-sticky-header">
                <div className="edit-schedule-header-left">
                  <h3>Edit Schedule</h3>
                  <span>Update this employee's schedule</span>
                </div>

                <div className="edit-schedule-header-actions">
                  <button
                    type="button"
                    className="edit-schedule-cancel-btn"
                    disabled={isSaving}
                    onClick={async () => {
                      setShowEditPanel(false);
                      setShowConfirmModal(false);
                      setEditingScheduleId(null);
                      setDateRange([null, null]);
                      setShifts(EMPTY_WEEK);

                      // restore leave map for visible overview week
                      if (weeklyOverviewStart) {
                        await loadWeeklyLeaveMap(weeklyOverviewStart);
                      }
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="edit-schedule-update-btn"
                    disabled={isSaving}
                    onClick={() => setShowConfirmModal(true)}
                  >
                    {isSaving ? "Updating…" : "Update Schedule"}
                  </button>
                </div>
              </div>

              <CreateScheduleSection
                employees={employees}
                selectedEmployee={selectedEmployee}
                setSelectedEmployee={setSelectedEmployee}
                selectedEmployees={selectedEmployees}
                setSelectedEmployees={setSelectedEmployees}
                multiAssign={multiAssign}
                setMultiAssign={setMultiAssign}
                startDate={startDate}
                endDate={endDate}
                dateRange={dateRange}
                setDateRange={setDateRange}
                repeatWeekly={repeatWeekly}
                setRepeatWeekly={setRepeatWeekly}
                shifts={shifts}
                setShifts={setShifts} // ✅ add this
                handleShiftChange={handleShiftChange}
                addActivity={addActivity}
                updateActivity={updateActivity}
                removeActivity={removeActivity}
                parseLocalDate={parseLocalDate}
                showConfirmModal={showConfirmModal}
                setShowConfirmModal={setShowConfirmModal}
                editingScheduleId={editingScheduleId}
                handleSaveSchedule={handleSaveSchedule}
                activityTypes={activityTypes}
                onCreateActivityType={createActivityType}
                onOpenTypesModal={() => setShowTypesModal(true)}
                isSaving={isSaving}
                isTypesLoading={isTypesLoading}
                toMin={toMin}
                weeklyLeaveMap={weeklyLeaveMap}
                locations={locations}
                showWorksiteDropdown={showWorksiteDropdown}
                primaryLocation={primaryLocation}
              />
            </div>
          </div>
        )}

        <ToastContainer />
      </div>
      {limitsOpen && (
        <div
          className="hs-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !savingLimits) {
              setLimitsOpen(false);
            }
          }}
        >
          <div
            className="hs-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hs-title"
          >
            {/* HEADER */}
            <div className="hs-header">
              <div className="hs-header-left">
                <div className="hs-header-icon">
                  <Clock3 size={25} strokeWidth={2} />
                </div>

                <div>
                  <div id="hs-title" className="hs-title">
                    Hours & Budget Caps
                  </div>

                  <div className="hs-sub">
                    Manage how many hours can be scheduled.
                  </div>

                  <div className="hs-timezone">
                    <Globe2 size={15} />
                    <span>Time zone:</span>
                    <strong>{companyTimeZone}</strong>
                  </div>
                </div>
              </div>

              <button
                className="hs-close"
                type="button"
                aria-label="Close hours settings"
                onClick={() => setLimitsOpen(false)}
                disabled={savingLimits}
              >
                ✕
              </button>
            </div>

            <div className="hs-body">
              {/* INFO */}
              <div className="hs-banner">
                <div className="hs-banner-icon">
                  <Info size={20} />
                </div>

                <div>
                  Set hour limits to help plan labor costs and prevent
                  over-scheduling.
                  <br />
                  Caps can be applied weekly or monthly at the company or
                  employee level.
                </div>
              </div>

              {/* SECTION HEADING */}
              <div className="hs-section-heading">
                <h3>Set hour limits</h3>
                <p>Leave the value at 0 for no limit.</p>
              </div>

              {/* COMPANY LIMITS */}
              <div className="hs-limit-row">
                <div className="hs-limit-info">
                  <div className="hs-limit-icon">
                    <Building2 size={22} />
                  </div>

                  <div>
                    <div className="hs-limit-title">Company limits</div>
                    <div className="hs-limit-description">
                      Apply limits to the entire company.
                    </div>
                  </div>
                </div>

                <div className="hs-limit-field">
                  <label htmlFor="company-week-cap">Weekly hour cap</label>

                  <div className="hs-input">
                    <input
                      id="company-week-cap"
                      type="number"
                      min="0"
                      step="0.25"
                      value={limitSettings.companyWeekCap}
                      onChange={(e) =>
                        setLimitSettings((s) => ({
                          ...s,
                          companyWeekCap: Number(e.target.value || 0),
                        }))
                      }
                      inputMode="decimal"
                    />

                    <span className="hs-suffix">hrs</span>
                  </div>

                  <span className="hs-help">Leave at 0 for no limit</span>
                </div>

                <div className="hs-limit-field">
                  <label htmlFor="company-month-cap">Monthly hour cap</label>

                  <div className="hs-input">
                    <input
                      id="company-month-cap"
                      type="number"
                      min="0"
                      step="0.25"
                      value={limitSettings.companyMonthCap}
                      onChange={(e) =>
                        setLimitSettings((s) => ({
                          ...s,
                          companyMonthCap: Number(e.target.value || 0),
                        }))
                      }
                      inputMode="decimal"
                    />

                    <span className="hs-suffix">hrs</span>
                  </div>

                  <span className="hs-help">Leave at 0 for no limit</span>
                </div>
              </div>

              {/* EMPLOYEE LIMITS */}
              <div className="hs-limit-row">
                <div className="hs-limit-info">
                  <div className="hs-limit-icon">
                    <Users size={22} />
                  </div>

                  <div>
                    <div className="hs-limit-title">Per-employee limits</div>
                    <div className="hs-limit-description">
                      Apply limits to each individual employee.
                    </div>
                  </div>
                </div>

                <div className="hs-limit-field">
                  <label htmlFor="employee-week-cap">Weekly hour cap</label>

                  <div className="hs-input">
                    <input
                      id="employee-week-cap"
                      type="number"
                      min="0"
                      step="0.25"
                      value={limitSettings.perEmpWeekCap}
                      onChange={(e) =>
                        setLimitSettings((s) => ({
                          ...s,
                          perEmpWeekCap: Number(e.target.value || 0),
                        }))
                      }
                      inputMode="decimal"
                    />

                    <span className="hs-suffix">hrs</span>
                  </div>

                  <span className="hs-help">Leave at 0 for no limit</span>
                </div>

                <div className="hs-limit-field">
                  <label htmlFor="employee-month-cap">Monthly hour cap</label>

                  <div className="hs-input">
                    <input
                      id="employee-month-cap"
                      type="number"
                      min="0"
                      step="0.25"
                      value={limitSettings.perEmpMonthCap}
                      onChange={(e) =>
                        setLimitSettings((s) => ({
                          ...s,
                          perEmpMonthCap: Number(e.target.value || 0),
                        }))
                      }
                      inputMode="decimal"
                    />

                    <span className="hs-suffix">hrs</span>
                  </div>

                  <span className="hs-help">Leave at 0 for no limit</span>
                </div>
              </div>

              {/* ENFORCE */}
              <label className="hs-enforce-card">
                <div className="hs-enforce-left">
                  <div className="hs-enforce-icon">
                    <ShieldCheck size={22} />
                  </div>

                  <div>
                    <div className="hs-enforce-title">Enforce hour caps</div>

                    <div className="hs-enforce-description">
                      When enabled, schedules that exceed limits will be
                      blocked.
                      <br />
                      When disabled, you'll see a warning and can choose to
                      proceed.
                    </div>
                  </div>
                </div>

                <div className="hs-switch">
                  <input
                    type="checkbox"
                    checked={!!limitSettings.enforce}
                    onChange={(e) =>
                      setLimitSettings((s) => ({
                        ...s,
                        enforce: e.target.checked,
                      }))
                    }
                  />

                  <span className="hs-toggle" />
                </div>
              </label>
            </div>

            {/* FOOTER */}
            <div className="hs-footer">
              <button
                type="button"
                className="hs-cancel-btn"
                onClick={() => setLimitsOpen(false)}
                disabled={savingLimits}
              >
                Cancel
              </button>

              <button
                type="button"
                className="hs-save-btn"
                onClick={() => saveLimitSettings({})}
                disabled={savingLimits}
              >
                <Save size={18} />

                {savingLimits ? "Saving…" : "Save settings"}
              </button>
            </div>
          </div>
        </div>
      )}
      <ManageActivityTypesModal
        open={showTypesModal}
        onClose={() => setShowTypesModal(false)}
        types={activityTypes}
        onCreate={createActivityType}
        onUpdate={updateActivityType}
        onDelete={deleteActivityType}
        isLoading={isTypesLoading}
      />
      <LimitsWarningModal
        open={limitsWarning.open}
        violations={limitsWarning.lines}
        isProceeding={proceedingAfterWarning}
        onProceed={() => {
          if (pendingContinue.current) pendingContinue.current();
        }}
        onCancel={() => setLimitsWarning({ open: false, lines: [] })}
      />
      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        body={confirm.body}
        danger={confirm.danger}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm((c) => ({ ...c, open: false }))}
      />
    </div>
  );
}
